import { useGetPartnerQuery } from '@/api/api.slice.tsx';
import { getApplicationState, setIsLogged } from '@/app/Application.slice.ts';
import { useAppDispatch, useAppSelector } from '@/app/hooks.ts';
import { AuthToken } from '@/features/login/AuthToken.tsx';
import i18n from '@/i18n/i18n.ts';
import { Loader, toast } from '@arsnovasystems/ars-front-lib';
import { Box } from '@mui/material';
import { ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router';

/**
 * Wrapper that will check if the user is already logged
 *
 * @param props          Props
 * @param props.redirect where to redirect if not logged
 * @param props.graceful if true, will not display the session expired alert
 * @param props.children the children to render if logged
 *
 * @returns              CheckAuth component
 */
export const CheckAuth = ({
  redirect,
  graceful,
  children,
}: {
  redirect: string;
  graceful: boolean;
  children: ReactNode | ReactNode[];
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const {
    data: me,
    isSuccess,
    isUninitialized,
    isLoading,
  } = useGetPartnerQuery();
  const { isLogged } = useAppSelector(getApplicationState);

  useEffect(() => {
    //app state does not match the server state
    if (isSuccess && !isLogged) {
      dispatch(setIsLogged(true));
    }
    if (isSuccess) {
      void i18n.changeLanguage(me.defaultLang);
    }
  }, [isSuccess, isLogged, me, dispatch]);

  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');
  const tokenType = urlParams.get('type');
  const hasToken = token && tokenType;

  // already logged
  if (isSuccess) {
    return <>{children}</>;
  }

  if (isUninitialized || isLoading) {
    return (
      <Box className='not-logged'>
        <Loader />
      </Box>
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!isUninitialized && !isSuccess && !isLoading && !hasToken) {
    toast({
      title: t('ik.lib.messageDispatcher.alert.title.sessionExpired'),
      description: t(
        'ik.lib.messageDispatcher.alert.description.sessionExpired',
      ),
    });
    return <Navigate to={redirect} />;
  }

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (hasToken && !isUninitialized && !isSuccess && !isLoading) {
    return (
      <AuthToken
        redirect={redirect}
        token={token}
        tokenType={tokenType}
        graceful={graceful}
      >
        {children}
      </AuthToken>
    );
  }

  return null;
};
